<template>
    <div ref="placement"
        class="font-sans">
        <teleport v-if="props.visible"
            to="body">
            <div v-on-click-outside="() => emit('update:visible', false)"
                class="absolute -translate-y-full z-50"
                :style="{
                    left: left,
                    top: top,
                }">
                <emoji-picker :data="emojiIndex"
                    :showPreview="false"
                    native
                    autoFocus
                    :perLine="8"
                    :emojiSize="20"
                    @select="select" />
            </div>

        </teleport>
    </div>
</template>

<script setup>
import { Picker as EmojiPicker, EmojiIndex } from 'emoji-mart-vue-fast/src';
import data from "emoji-mart-vue-fast/data/all.json";
import { nextTick, onMounted, ref, watch, watchEffect } from 'vue';
import { useEventListener } from '@vueuse/core'
import debounce from '~/utils/debounce';
import { vOnClickOutside } from '@vueuse/components'

const emojiIndex = ref(new EmojiIndex(data));

const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
        default: false,
    }
});

const emit = defineEmits(['selected', 'update:visible']);

const select = (event) => {
    emit('selected', event);
    emit('update:visible', false);
}

const placement = ref();
const left = ref(0);
const top = ref(0);

const placeEmojiPicker = () => {
    if (!placement.value) {
        return;
    }

    const position = placement.value.getBoundingClientRect();
    left.value = `${position.left}px`;
    top.value = `${position.bottom + window.scrollY}px`;
}

useEventListener(window, 'scrollend', debounce(placeEmojiPicker, 150));
useEventListener(window, 'resize', debounce(placeEmojiPicker, 150));
watchEffect(debounce(placeEmojiPicker, 150));
watch(() => props.visible, debounce(placeEmojiPicker, 25));

onMounted(() => nextTick(placeEmojiPicker));

</script>

<style scoped>
@reference "@css/style.css";

:deep(.emoji-mart) {
    @apply border font-sans border-solid border-white bg-white shadow-xl rounded-lg !w-[320px] !max-w-full !h-[320px];
}

:deep(.emoji-mart-search) {
    @apply m-0 px-4 pt-4 pb-1;
}

:deep(.emoji-mart-scroll) {
    @apply px-4;
}

:deep(.emoji-mart-search input) {
    @apply font-sans h-12 rounded-xl border-slate-300 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-50;
}

:deep(.emoji-mart-category) {
    @apply grid grid-cols-8 pt-4;
}

:deep(.emoji-mart-category.emoji-mart-no-results) {
    @apply flex text-center w-full;
}

:deep(.emoji-mart-category-label) {
    @apply col-span-8 font-sans text-sm font-bold text-black mb-0 py-0;
}


:deep(.emoji-mart-category .emoji-mart-emoji span) {
    @apply cursor-pointer;
}

:deep(.emoji-mart-bar.emoji-mart-bar-anchors) {
    @apply order-3 bg-slate-50 rounded-b-lg border-0;
}

:deep(.emoji-mart-anchor) {
    @apply text-slate-800;
}

:deep(.emoji-mart-anchor.emoji-mart-anchor-selected) {
    @apply !text-sky-500;
}

:deep(.emoji-mart-anchor-bar) {
    @apply hidden;
}
</style>
